import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import { GET_PIE_CHART } from "../../../../services/ApiCalls";
import { Spinner } from "react-bootstrap";
import { checkResponse } from "../../../../utilities/utilities";

const toSafeLabel = (value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
};

const toSafeNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const BookingChart = () => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState("today");
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ---------------- Tabs ---------------- */
  const periodTabs = [
    { key: "today", label: t("dashboard.today") },
    { key: "yesterday", label: t("dashboard.yesterday") },
    { key: "thisWeek", label: t("dashboard.thisWeek") },
    { key: "thisMonth", label: t("dashboard.thisMonth") },
    { key: "lastMonth", label: t("dashboard.lastMonth") },
  ];

  /* ---------------- API CALL ---------------- */
  useEffect(() => {
    setLoading(true);
    GET_PIE_CHART({ filter: activeTab }).then((res) => {
      checkResponse({
        res,
        setData: (data) => {
          setChartData(data || []);
          setLoading(false);
        },
        setLoader: setLoading,
      });
    });
  }, [activeTab]);

  /* ---------------- Chart Processing ---------------- */
  const { series, options, bookingTypes } = useMemo(() => {
    const colorsArr = [
      "#e8307a",
      "#ff5c5c",
      "#09a86a",
      "#4c5eff",
      "#ffa500",
      "#0088cc",
    ];

    const safeChartData = (Array.isArray(chartData) ? chartData : [])
      .map((item, index) => ({
        label: toSafeLabel(
          item?.label ?? item?._id ?? item?.type,
          `${t("dashboard.bookingType")} ${index + 1}`
        ),
        percentage: toSafeNumber(item?.percentage ?? item?.count ?? item?.value),
      }))
      .filter((item) => item.percentage > 0);

    const labels = safeChartData.map((d) => d.label);
    const seriesData = safeChartData.map((d) => d.percentage);
    const noDataLabel = toSafeLabel(
      t("dashboard.common.noDataAvailable", "No data available"),
      "No data available"
    );

    return {
      series: seriesData.length ? seriesData : [0],
      bookingTypes: safeChartData.map((item, i) => ({
        type: item.label,
        percentage: item.percentage,
        color: colorsArr[i % colorsArr.length],
      })),
      options: {
        chart: { type: "donut", height: 300 },
        labels: labels.length ? labels : [noDataLabel],
        colors: colorsArr,
        legend: {
          show: true,
          position: "bottom",
          fontSize: "12px",
          markers: { width: 10, height: 10, radius: 50 },
        },
        stroke: {
          width: 2,
          colors: ["#fff"],
        },
        plotOptions: {
          pie: {
            donut: { size: "60%" },
          },
        },
        dataLabels: { enabled: false },
      },
    };
  }, [chartData, t]);

  return (
    <div
      className="p-3 shadow-sm rounded-4 bg-white"
      style={{ border: "1px solid #f1f1f1" }}
    >
      <h6 className="fw-bold mb-2">{t("dashboard.bookingType")}</h6>

      {/* ---------------- Tabs ---------------- */}
      <div className="d-flex flex-wrap gap-3 mb-3">
        {periodTabs.map((tab) => (
          <span
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: activeTab === tab.key ? "bold" : "normal",
              color: activeTab === tab.key ? "#e8307a" : "#6c757d",
              borderBottom:
                activeTab === tab.key ? "2px solid #e8307a" : "none",
              paddingBottom: "4px",
            }}
          >
            {tab.label}
          </span>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-4">
          <Spinner size="sm" />
        </div>
      ) : (
        <div className="row align-items-center">
          {/* Chart */}
          <div className="col-md-6">
            <ReactApexChart
              options={options}
              series={series}
              type="donut"
              height={260}
            />
          </div>

          {/* Percentage List */}
          <div className="col-md-6">
            {bookingTypes.length > 0 ? (
              bookingTypes.map((booking) => (
                <div
                  key={booking.type}
                  className="d-flex align-items-center mb-3"
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: booking.color,
                      marginRight: "8px",
                    }}
                  />
                  <span
                    className="fw-bold me-auto"
                    style={{ fontSize: "14px" }}
                  >
                    {booking.type}
                  </span>
                  <span
                    className="fw-bold"
                    style={{ fontSize: "14px", color: booking.color }}
                  >
                    {booking.percentage}%
                  </span>
                </div>
              ))
            ) : (
              <div className="text-muted small">
                {t("dashboard.common.noDataAvailable")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingChart;
